import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("home");

  const [selectedImage, setSelectedImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);

  // 🔥 ACTIVITY
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [category, setCategory] = useState("");

  // 🔥 NEWS
  const [newsTitle, setNewsTitle] = useState("");
  const [newsDesc, setNewsDesc] = useState("");
  const [newsImage, setNewsImage] = useState(null);
  const [newsPreview, setNewsPreview] = useState(null);

  // 🔥 GALLERY
  const [galleryImage, setGalleryImage] = useState(null);
  const [galleryPreview, setGalleryPreview] = useState(null);

  // 🔥 COMMITTEE
  const [memberName, setMemberName] = useState("");
  const [memberRole, setMemberRole] = useState("");
  const [memberImage, setMemberImage] = useState(null);
  const [memberPreview, setMemberPreview] = useState(null);

  // 🔥 ABOUT
  const [aboutImages, setAboutImages] = useState([]);
  const [aboutPreviews, setAboutPreviews] = useState([]);

  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const API = process.env.PARCEL_API_URL;

  // 🔥 AUTO HIDE POPUP
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // 🔹 FETCH HERO IMAGE
  useEffect(() => {
    if (!API) return;

    axios.get(`${API}/api/admin/hero`).then((res) => {
      setCurrentImage(res.data.imageUrl + "?t=" + Date.now());
    });
  }, [API]);

  // 🔹 FILE HANDLERS
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleNewsImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setNewsImage(file);
    setNewsPreview(URL.createObjectURL(file));
  };

  const handleGalleryImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setGalleryImage(file);
    setGalleryPreview(URL.createObjectURL(file));
  };

  const handleMemberImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMemberImage(file);
    setMemberPreview(URL.createObjectURL(file));
  };

  const handleAboutImages = (e) => {
    const files = Array.from(e.target.files);
    setAboutImages(files);
    setAboutPreviews(files.map((f) => URL.createObjectURL(f)));
  };

  // 🔹 HERO
  const handleUpload = async () => {
    if (!selectedImage) {
      setMessage({ type: "error", text: "Select an image first" });
      return;
    }

    const formData = new FormData();
    formData.append("image", selectedImage);

    try {
      await axios.post(`${API}/api/admin/upload-hero`, formData, {
        headers: { Authorization: localStorage.getItem("token") },
      });

      setMessage({ type: "success", text: "Hero updated" });
      setPreview(null);
      setSelectedImage(null);
    } catch {
      setMessage({ type: "error", text: "Upload failed" });
    }
  };

  // 🔥 ACTIVITY
  const handleCreateActivity = async () => {
    if (!name || !desc || !category || !selectedImage) {
      setMessage({ type: "error", text: "Fill all fields" });
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("desc", desc);
    formData.append("category", category);
    formData.append("image", selectedImage);

    await axios.post(`${API}/api/activities/create`, formData, {
      headers: { Authorization: localStorage.getItem("token") },
    });

    setMessage({ type: "success", text: "Activity created" });
  };

  // 🔥 NEWS
  const handleCreateNews = async () => {
    if (!newsTitle || !newsDesc || !newsImage) {
      setMessage({ type: "error", text: "Fill all fields" });
      return;
    }

    const formData = new FormData();
    formData.append("title", newsTitle);
    formData.append("desc", newsDesc);
    formData.append("image", newsImage);

    await axios.post(`${API}/api/news/create`, formData, {
      headers: { Authorization: localStorage.getItem("token") },
    });

    setMessage({ type: "success", text: "News created" });
  };

  // 🔥 GALLERY
  const handleCreateGallery = async () => {
    if (!galleryImage) {
      setMessage({ type: "error", text: "Select an image" });
      return;
    }

    const formData = new FormData();
    formData.append("image", galleryImage);

    await axios.post(`${API}/api/gallery/create`, formData, {
      headers: { Authorization: localStorage.getItem("token") },
    });

    setMessage({ type: "success", text: "Gallery uploaded" });
  };

  // 🔥 COMMITTEE
  const handleCreateMember = async () => {
    if (!memberName || !memberRole || !memberImage) {
      setMessage({ type: "error", text: "Fill all fields" });
      return;
    }

    const formData = new FormData();
    formData.append("name", memberName);
    formData.append("role", memberRole);
    formData.append("image", memberImage);

    try {
      await axios.post(`${API}/api/committee/create`, formData, {
        headers: { Authorization: localStorage.getItem("token") },
      });

      setMessage({ type: "success", text: "Member added successfully 🎉" });

      setMemberName("");
      setMemberRole("");
      setMemberImage(null);
      setMemberPreview(null);
    } catch {
      setMessage({ type: "error", text: "Failed to add member" });
    }
  };

  const handleUploadAbout = async () => {
    if (aboutImages.length === 0) {
      setMessage({ type: "error", text: "Select images" });
      return;
    }

    const formData = new FormData();
    aboutImages.forEach((img) => formData.append("images", img));

    try {
      await axios.post(`${API}/api/about/upload`, formData, {
        headers: { Authorization: localStorage.getItem("token") },
      });

      setMessage({ type: "success", text: "About images uploaded 🎉" });

      setAboutImages([]);
      setAboutPreviews([]);
    } catch {
      setMessage({ type: "error", text: "Upload failed" });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/sgya-admin-secure-login");
  };

  return (
    <div className="bg-gray-100 min-h-screen flex">
      {/* POPUP */}
      {message && (
        <div
          className={`fixed top-4 right-4 px-4 py-2 text-white rounded ${
            message.type === "success" ? "bg-green-500" : "bg-red-500"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* SIDEBAR */}
      <div className="w-64 bg-white shadow-lg p-5">
        <h2 className="text-2xl font-bold text-orange-600 mb-6">Admin Panel</h2>

        <ul className="space-y-3">
          {["home", "activities", "news", "gallery", "committee", "about"].map(
            (tab) => (
              <li
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`cursor-pointer p-2 rounded ${
                  activeTab === tab
                    ? "bg-orange-100 text-orange-600"
                    : "hover:bg-gray-100"
                }`}
              >
                {tab}
              </li>
            ),
          )}
        </ul>

        <button
          onClick={handleLogout}
          className="mt-6 bg-red-500 text-white w-full py-2 rounded"
        >
          Logout
        </button>
      </div>

      {/* MAIN */}
      <div className="flex-1 p-6">
        {/* HOME */}
        {activeTab === "home" && (
          <div className="bg-white p-5 rounded shadow">
            <h3 className="font-bold mb-3">Hero Image</h3>
            {currentImage && (
              <img src={currentImage} className="mb-4 rounded" />
            )}
            <input type="file" onChange={handleFileChange} />
            {preview && <img src={preview} className="mt-2 h-32" />}
            <button
              onClick={handleUpload}
              className="mt-3 bg-orange-500 text-white px-4 py-2"
            >
              Upload
            </button>
          </div>
        )}

        {/* ACTIVITIES */}
        {activeTab === "activities" && (
          <div className="bg-white p-5 rounded shadow">
            <h3 className="font-bold mb-3">Create Activity</h3>
            <input
              placeholder="Name"
              className="border p-2 w-full mb-2"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <textarea
              placeholder="Description"
              className="border p-2 w-full mb-2"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
            />
            <select
              className="border p-2 w-full mb-2"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">Select Category</option>
              <option value="charity">Charity</option>
              <option value="youth">Youth</option>
              <option value="events">Events</option>
            </select>
            <input type="file" onChange={handleFileChange} />
            {preview && <img src={preview} className="mt-2 h-32" />}
            <button
              onClick={handleCreateActivity}
              className="mt-3 bg-orange-500 text-white px-4 py-2"
            >
              Create Activity
            </button>
          </div>
        )}

        {/* NEWS */}
        {activeTab === "news" && (
          <div className="bg-white p-5 rounded shadow">
            <h3 className="font-bold mb-3">Create News</h3>
            <input
              placeholder="Title"
              className="border p-2 w-full mb-2"
              value={newsTitle}
              onChange={(e) => setNewsTitle(e.target.value)}
            />
            <textarea
              placeholder="Description"
              className="border p-2 w-full mb-2"
              value={newsDesc}
              onChange={(e) => setNewsDesc(e.target.value)}
            />
            <input type="file" onChange={handleNewsImage} />
            {newsPreview && <img src={newsPreview} className="mt-2 h-32" />}
            <button
              onClick={handleCreateNews}
              className="mt-3 bg-orange-500 text-white px-4 py-2"
            >
              Create News
            </button>
          </div>
        )}

        {/* GALLERY */}
        {activeTab === "gallery" && (
          <div className="bg-white p-5 rounded shadow">
            <h3 className="font-bold mb-3">Upload Gallery Image</h3>
            <input type="file" onChange={handleGalleryImage} />
            {galleryPreview && (
              <img src={galleryPreview} className="mt-2 h-32 rounded" />
            )}
            <button
              onClick={handleCreateGallery}
              className="mt-3 bg-orange-500 text-white px-4 py-2"
            >
              Upload Image
            </button>
          </div>
        )}

        {/* COMMITTEE */}
        {activeTab === "committee" && (
          <div className="bg-white p-5 rounded shadow max-w-md">
            <h3 className="font-bold mb-3 text-orange-600">
              Add Committee Member
            </h3>

            <input
              placeholder="Name"
              className="border p-2 w-full mb-2"
              value={memberName}
              onChange={(e) => setMemberName(e.target.value)}
            />

            <input
              placeholder="Role"
              className="border p-2 w-full mb-2"
              value={memberRole}
              onChange={(e) => setMemberRole(e.target.value)}
            />

            <input type="file" onChange={handleMemberImage} />

            {memberPreview && (
              <img
                src={memberPreview}
                className="mt-2 h-32 w-32 rounded-full object-cover"
              />
            )}

            <button
              onClick={handleCreateMember}
              className="mt-3 bg-orange-500 text-white px-4 py-2 w-full"
            >
              Add Member
            </button>
          </div>
        )}

        {/* ABOUT */}
        {activeTab === "about" && (
          <div className="bg-white p-5 rounded shadow max-w-md">
            <h3 className="font-bold mb-3 text-orange-600">
              Upload About Images (Max 2)
            </h3>

            <input type="file" multiple onChange={handleAboutImages} />

            {/* PREVIEW */}
            <div className="flex gap-3 mt-3">
              {aboutPreviews.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  className="h-24 w-24 object-cover rounded"
                />
              ))}
            </div>

            <button
              onClick={handleUploadAbout}
              className="mt-3 bg-orange-500 text-white px-4 py-2 w-full"
            >
              Upload Images
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
